import React from "react";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";
import UserModal from "../UserModal";
import useUserManager from "../logic/useUserManager";

export default function UserManager({
  currentUser,
  organizations = [],
  activeOrgId,
}) {
  const { theme } = useTheme();

  const {
    users,
    loading,
    isPlatformAdmin,
    activeOrg,
    showUserModal,
    setShowUserModal,
    editingUser,
    setEditingUser,
    submitUser,
    deleteUser,
  } = useUserManager({
    currentUser,
    organizations,
    activeOrgId,
  });

  const card =
    theme === "dark"
      ? "bg-neutral-750 border border-neutral-700 text-slate-100"
      : "bg-white border border-gray-200 text-gray-900";

  return (
    <div className={`${card} rounded-xl shadow p-6`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users</h1>

        {(isPlatformAdmin || activeOrg) && (
          <button
            onClick={() => {
              setEditingUser(null);
              setShowUserModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
          >
            <FiPlus /> Add User
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-gray-400 text-sm mb-4">Loading users…</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-300 dark:border-slate-700">
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Role</th>
              <th className="py-2 px-3">Organization</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {users.map((u) => {
              const membership = u.memberships?.[0];
              const orgName = membership?.organization?.name || "—";
              const role = membership?.role || "";

              return (
                <tr
                  key={u.id}
                  className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <td className="py-2 px-3">{u.name}</td>
                  <td className="py-2 px-3">{u.email}</td>
                  <td className="py-2 px-3">
                    {u.global_role === "platform_admin"
                      ? "Platform Admin"
                      : role || "—"}
                  </td>
                  <td className="py-2 px-3">{orgName}</td>

                  <td className="py-2 px-3 flex gap-3">
                    <button
                      onClick={() => {
                        setEditingUser(u);
                        setShowUserModal(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <FiEdit />
                    </button>

                    {(isPlatformAdmin || activeOrg) && (
                      <button
                        onClick={() => deleteUser(u.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <UserModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
        mode={editingUser ? "edit" : "add"}
        initialUser={editingUser}
        organizations={organizations}
        currentUser={currentUser}
        onSubmit={submitUser}
      />
    </div>
  );
}

